import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { DownloadOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import {
  Button,
  Checkbox,
  ConfigProvider,
  Flex,
  Input,
  Select,
  Switch,
  Typography,
  theme,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useFlags } from 'launchdarkly-react-client-sdk';
import LoaderEcho from '@/components/LoaderEcho';
import ModalEcho from '@/components/modals/Modal/ModalEcho';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';
import DataTableEcho from '@/components/tables/DataTable/DataTableEcho';
import { useDerivedNetworkContext } from '@/context';
import { useColorAssignment } from '@/hooks/useColorAssignment';
import { usePlayerDownloadsQuery, useTopPlayersQuery } from '@/services/revenueApi';
import type {
  ChartGranularity,
  DateRange,
  DownloadConfig,
  ShowFilter,
  TableColumn,
  TableData,
} from '@/types';
import type { PlayerTableRow } from '@/types/api/shows';
import { checkboxColors, hasHlsEnabledShow } from '@/utils';
import { formatPercentageTwoDp } from '@/utils/numberFormattingUtils';
import {
  trackAudienceTopPlayersModalDownloadClicked,
  trackAudienceTopPlayersModalGranularityChanged,
  trackAudienceTopPlayersModalHideSocialsToggled,
  trackAudienceTopPlayersModalOpened,
  trackAudienceTopPlayersModalRowSelectionToggled,
  trackAudienceTopPlayersModalSelectAllToggled,
} from './audienceModalMixpanel';
import { getDisplayLabel, isSocialTopPlayerLabel } from './topPlayerCategories';
import TopPlayersModalChartEcho from './TopPlayersModalChartEcho';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useIsBeforeDataAvailability from './useIsBeforeDataAvailability';
import useTopPlayersCsvExport from './useTopPlayersCsvExport';

const DEFAULT_SELECTED_COUNT = 5;
const DEFAULT_PAGE_SIZE = 10;
const EMPTY_SELECTED_ROWS: string[] = [];
const DEFAULT_GRAPH_GRANULARITY: ChartGranularity = 'day';
const GRANULARITY_SELECT_WIDTH = 128;
const CHECKBOX_COLUMN_WIDTH = 40;

const numberFormatter = new Intl.NumberFormat('en');

const topPlayersGranularityOptions: Array<{ value: ChartGranularity; label: string }> = [
  { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' },
  { value: 'month', label: 'Monthly' },
];

interface TopPlayersModalContentProps {
  dateRange: DateRange;
  selectedShow: ShowFilter;
  granularity: ChartGranularity;
  excludeSocials: boolean;
  showSocialViews: boolean;
  onExcludeSocialsChange: (excludeSocials: boolean) => void;
  onGranularityChange: (granularity: ChartGranularity) => void;
}

interface SelectableTopPlayerTableRow extends Omit<PlayerTableRow, 'label'> {
  id: string;
  label: string;
  metricsKey: string;
}

const formatCount = (value: number): string => numberFormatter.format(Math.round(value));

const formatPercentage = (value: number): string => `${formatPercentageTwoDp(value)}%`;

const formatNullableCount = (value: number | null): string =>
  value === null ? 'N/A' : formatCount(value);

const formatNullablePercentage = (value: number | null): string =>
  value === null ? 'N/A' : formatPercentage(value);

const getTopPlayerMetricsKey = (label: string | null | undefined): string => String(label);

const getTopPlayerId = (metricsKey: string): string => encodeURIComponent(metricsKey);

const haveSameOrderedRows = (firstRows: string[], secondRows: string[]): boolean =>
  firstRows.length === secondRows.length &&
  firstRows.every((rowId, index) => rowId === secondRows[index]);

const withSelectableIds = (rows: PlayerTableRow[]): SelectableTopPlayerTableRow[] =>
  rows.map((row) => {
    const metricsKey = getTopPlayerMetricsKey(row.label);

    return {
      ...row,
      id: getTopPlayerId(metricsKey),
      label: getDisplayLabel(row.label),
      metricsKey,
    };
  });

const buildTableData = (rows: SelectableTopPlayerTableRow[], hasVideoData: boolean): TableData[] =>
  rows.map((row) => {
    const videoColumns: TableColumn[] = hasVideoData
      ? [
          { id: 'audioDownloads', value: formatNullableCount(row.audioDownloads), type: 'number' },
          { id: 'audioShare', value: formatNullablePercentage(row.audioShare), type: 'percentage' },
          { id: 'videoViews', value: formatNullableCount(row.videoViews), type: 'number' },
          { id: 'videoShare', value: formatNullablePercentage(row.videoShare), type: 'percentage' },
        ]
      : [];

    return {
      id: row.id,
      columns: [
        { id: 'label', value: row.label, type: 'string' },
        {
          id: 'downloadsOrViews',
          value: formatCount(row.downloadsOrViews),
          type: 'number',
        },
        { id: 'totalPercentage', value: formatPercentage(row.totalPercentage), type: 'percentage' },
        ...videoColumns,
        { id: 'appUsage', value: formatNullablePercentage(row.appUsage), type: 'percentage' },
        {
          id: 'websiteUsage',
          value: formatNullablePercentage(row.websiteUsage),
          type: 'percentage',
        },
        { id: 'otherUsage', value: formatNullablePercentage(row.otherUsage), type: 'percentage' },
      ],
    };
  });

const getCellValue = (record: TableData, columnKey: string) =>
  record.columns.find((column) => column.id === columnKey)?.value ?? null;

const ColoredSelectionCheckbox = ({
  checked,
  color,
  ariaLabel,
  onChange,
}: {
  checked: boolean;
  color?: string;
  ariaLabel: string;
  onChange: () => void;
}) => {
  const checkbox = <Checkbox checked={checked} aria-label={ariaLabel} onChange={onChange} />;

  if (!checked || !color) {
    return checkbox;
  }

  return <ConfigProvider theme={{ token: { colorPrimary: color } }}>{checkbox}</ConfigProvider>;
};

const TopPlayersModalContent = ({
  dateRange,
  selectedShow,
  granularity,
  excludeSocials,
  showSocialViews,
  onExcludeSocialsChange,
  onGranularityChange,
}: TopPlayersModalContentProps) => {
  const { token } = theme.useToken();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { audienceSources } = useFlags<{ audienceSources?: string[] }>();
  const sources = audienceSources ?? ['acast', 'phyllo'];
  const effectiveExcludeSocials = excludeSocials || !showSocialViews;
  const showIds = selectedShow !== 'all' && selectedShow ? [selectedShow] : undefined;
  const hasVideoData = hasHlsEnabledShow([selectedNetwork], showIds);
  const [selectedRows, setSelectedRows] = useState<string[] | null>(null);
  const selectedRowsRef = useRef(selectedRows);
  selectedRowsRef.current = selectedRows;
  const [topPlayerSearch, setTopPlayerSearch] = useState('');
  const [tablePage, setTablePage] = useState(1);

  const { data, isPending } = useTopPlayersQuery(
    selectedNetwork.id,
    dateRange.from,
    dateRange.to,
    undefined,
    { showIds, sources, limit: -1, excludeSocials: effectiveExcludeSocials }
  );

  const topPlayerRows = useMemo(
    () =>
      withSelectableIds(data?.tableRows ?? []).filter(
        (row) => showSocialViews || !isSocialTopPlayerLabel(row.metricsKey)
      ),
    [data?.tableRows, showSocialViews]
  );
  const topPlayerRowIds = useMemo(() => topPlayerRows.map((row) => row.id), [topPlayerRows]);
  const normalizedTopPlayerSearch = topPlayerSearch.trim().toLowerCase();
  const filteredTopPlayerRows = useMemo(() => {
    if (!normalizedTopPlayerSearch) {
      return topPlayerRows;
    }

    return topPlayerRows.filter((row) =>
      row.label.toLowerCase().includes(normalizedTopPlayerSearch)
    );
  }, [normalizedTopPlayerSearch, topPlayerRows]);
  const tableData = useMemo(
    () => buildTableData(filteredTopPlayerRows, hasVideoData),
    [filteredTopPlayerRows, hasVideoData]
  );

  useEffect(() => {
    setTablePage(1);
  }, [normalizedTopPlayerSearch]);

  useEffect(() => {
    setSelectedRows((currentRows) => {
      if (topPlayerRowIds.length === 0) {
        return currentRows === null || currentRows.length === 0 ? currentRows : EMPTY_SELECTED_ROWS;
      }

      const availableRowIds = new Set(topPlayerRowIds);
      const validSelectedRows = (currentRows ?? []).filter((rowId) => availableRowIds.has(rowId));
      const nextSelectedRows =
        validSelectedRows.length > 0
          ? validSelectedRows
          : topPlayerRowIds.slice(0, DEFAULT_SELECTED_COUNT);

      return currentRows !== null && haveSameOrderedRows(currentRows, nextSelectedRows)
        ? currentRows
        : nextSelectedRows;
    });
  }, [topPlayerRowIds]);

  const activeSelectedRows = selectedRows ?? EMPTY_SELECTED_ROWS;
  const deferredSelectedRows = useDeferredValue(activeSelectedRows);
  const { getColor } = useColorAssignment(activeSelectedRows);
  const visibleSelectedRows = useMemo(() => {
    const filteredRowIds = new Set(filteredTopPlayerRows.map((row) => row.id));
    return activeSelectedRows.filter((rowId) => filteredRowIds.has(rowId));
  }, [activeSelectedRows, filteredTopPlayerRows]);

  const selectedTopPlayers = useMemo(
    () =>
      deferredSelectedRows
        .map((id) => topPlayerRows.find((row) => row.id === id))
        .filter((row): row is SelectableTopPlayerTableRow => row != null),
    [deferredSelectedRows, topPlayerRows]
  );

  const { data: dailyMetrics } = usePlayerDownloadsQuery(
    selectedNetwork.id,
    dateRange.from,
    dateRange.to,
    { enabled: topPlayerRows.length > 0 },
    { showIds, sources, granularity }
  );

  const handleSelect = useCallback((id: string) => {
    const rows = selectedRowsRef.current ?? [];
    const isSelected = rows.includes(id);
    trackAudienceTopPlayersModalRowSelectionToggled(id, !isSelected);
    setSelectedRows((currentRows) => {
      const current = currentRows ?? [];
      return isSelected ? current.filter((rowId) => rowId !== id) : [...current, id];
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const rows = selectedRowsRef.current ?? [];
    const filteredRowIds = filteredTopPlayerRows.map((row) => row.id);
    const allFilteredRowsSelected =
      filteredRowIds.length > 0 && filteredRowIds.every((rowId) => rows.includes(rowId));

    trackAudienceTopPlayersModalSelectAllToggled(!allFilteredRowsSelected);
    setSelectedRows((currentRows) => {
      const current = currentRows ?? [];
      if (allFilteredRowsSelected) {
        return current.filter((rowId) => !filteredRowIds.includes(rowId));
      }

      return Array.from(new Set([...current, ...filteredRowIds]));
    });
  }, [filteredTopPlayerRows]);

  const allFilteredSelected =
    filteredTopPlayerRows.length > 0 &&
    filteredTopPlayerRows.every((row) => visibleSelectedRows.includes(row.id));
  const someFilteredSelected = visibleSelectedRows.length > 0 && !allFilteredSelected;

  const columns: ColumnsType<TableData> = useMemo(
    () => [
      {
        key: 'checkbox',
        width: CHECKBOX_COLUMN_WIDTH,
        title: (
          <Checkbox
            checked={allFilteredSelected}
            indeterminate={someFilteredSelected}
            aria-label="Select all apps and websites"
            onChange={handleSelectAll}
          />
        ),
        render: (_value, record) => {
          const colorIndex = getColor(record.id);
          const color = colorIndex != null ? checkboxColors[colorIndex] : undefined;
          const isSelected = activeSelectedRows.includes(record.id);

          return (
            <ColoredSelectionCheckbox
              checked={isSelected}
              color={isSelected ? color : undefined}
              ariaLabel={`Select ${getCellValue(record, 'label') ?? record.id}`}
              onChange={() => handleSelect(record.id)}
            />
          );
        },
      },
      {
        key: 'label',
        title: 'App and/or website',
        sorter: true,
        render: (_value, record) => getCellValue(record, 'label'),
      },
      {
        key: 'downloadsOrViews',
        title: hasVideoData ? 'Downloads or views' : 'Downloads',
        align: 'right',
        sorter: true,
        render: (_value, record) => getCellValue(record, 'downloadsOrViews'),
      },
      {
        key: 'totalPercentage',
        title: '% of total',
        align: 'right',
        sorter: true,
        render: (_value, record) => getCellValue(record, 'totalPercentage'),
      },
      ...(hasVideoData
        ? [
            {
              key: 'audioDownloads',
              title: 'Podcast audio downloads',
              align: 'right' as const,
              sorter: true,
              render: (_value: unknown, record: TableData) =>
                getCellValue(record, 'audioDownloads'),
            },
            {
              key: 'audioShare',
              title: 'Part of audio vs video',
              align: 'right' as const,
              sorter: true,
              render: (_value: unknown, record: TableData) => getCellValue(record, 'audioShare'),
            },
            {
              key: 'videoViews',
              title: 'Podcast video views',
              align: 'right' as const,
              sorter: true,
              render: (_value: unknown, record: TableData) => getCellValue(record, 'videoViews'),
            },
            {
              key: 'videoShare',
              title: 'Part of video vs audio',
              align: 'right' as const,
              sorter: true,
              render: (_value: unknown, record: TableData) => getCellValue(record, 'videoShare'),
            },
          ]
        : []),
      {
        key: 'appUsage',
        title: 'App usage',
        align: 'right',
        sorter: true,
        render: (_value, record) => getCellValue(record, 'appUsage'),
      },
      {
        key: 'websiteUsage',
        title: 'Website usage',
        align: 'right',
        sorter: true,
        render: (_value, record) => getCellValue(record, 'websiteUsage'),
      },
      {
        key: 'otherUsage',
        title: 'Other usage',
        align: 'right',
        sorter: true,
        render: (_value, record) => getCellValue(record, 'otherUsage'),
      },
    ],
    [
      activeSelectedRows,
      allFilteredSelected,
      getColor,
      handleSelect,
      handleSelectAll,
      hasVideoData,
      someFilteredSelected,
    ]
  );

  const isBeforeDataAvailability = useIsBeforeDataAvailability('players', dateRange.from);

  if (isBeforeDataAvailability) {
    return <DataAvailabilityBanner dataType="players" />;
  }

  if (isPending) {
    return (
      <Flex justify="center" align="center" style={{ height: '60vh' }}>
        <LoaderEcho data-testid="loader" />
      </Flex>
    );
  }

  if (!data || data.data.length < 1) {
    return <NoDataMessageEcho dataType="data" />;
  }

  return (
    <Flex vertical gap={token.marginLG} style={{ padding: token.padding }}>
      <TopPlayersModalChartEcho
        selectedRows={selectedTopPlayers}
        dailyMetrics={dailyMetrics}
        getColor={getColor}
        granularity={granularity}
      />
      <Flex align="center" gap={token.margin} wrap>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="Search apps and websites"
          aria-label="Search apps and websites"
          style={{ flex: 1, minWidth: 0 }}
          size="large"
          value={topPlayerSearch}
          onChange={(event) => {
            setTopPlayerSearch(event.target.value);
          }}
        />
        {showSocialViews && (
          <Flex align="center" gap={token.marginXS} flex="none">
            <Typography.Text>Hide socials</Typography.Text>
            <Switch
              id="toggle-hide-socials"
              checked={excludeSocials}
              onChange={onExcludeSocialsChange}
            />
          </Flex>
        )}
        <Select
          value={granularity}
          aria-label="Graph granularity"
          style={{ width: GRANULARITY_SELECT_WIDTH }}
          options={topPlayersGranularityOptions}
          onChange={onGranularityChange}
        />
      </Flex>
      <DataTableEcho
        columns={columns}
        dataSource={tableData}
        rowKey="id"
        dataTestId="top-players-modal-table"
        initialState={{ sorting: { key: 'downloadsOrViews', direction: 'desc' } }}
        pagination={{
          page: tablePage,
          onPageChange: setTablePage,
          summary: (fromPage, toPage, total) => `Showing ${fromPage}-${toPage} of ${total}`,
          defaultPageSize: DEFAULT_PAGE_SIZE,
          pageSizeOptions: [10, 25, 50],
        }}
      />
    </Flex>
  );
};

const TopPlayersModalEcho = () => {
  const [granularity, setGranularity] = useState<ChartGranularity>(DEFAULT_GRAPH_GRANULARITY);
  const [excludeSocials, setExcludeSocials] = useState(false);
  const { handleTopPlayersCSVExport } = useTopPlayersCsvExport();
  const { showAudienceSocialViews } = useFlags<{ showAudienceSocialViews?: boolean }>();
  const showSocialViews = showAudienceSocialViews === true;

  return (
    <ModalEcho
      hashProp="top-apps"
      title="All apps & websites"
      showDateRangeFilter
      onOpen={trackAudienceTopPlayersModalOpened}
      downloadConfig={{
        onDownload: (dateRange, selectedShow) => {
          trackAudienceTopPlayersModalDownloadClicked();
          return handleTopPlayersCSVExport(dateRange, selectedShow ?? 'all', granularity);
        },
        downloadButtonText: 'Export as CSV',
        downloadButtonIcon: DownloadOutlined as DownloadConfig['downloadButtonIcon'],
      }}
      renderTrigger={(openModal) => (
        <Flex justify="flex-end" style={{ alignSelf: 'flex-end' }}>
          <Button
            type="text"
            color="primary"
            variant="text"
            icon={<RightOutlined />}
            iconPlacement="end"
            onClick={openModal}
          >
            All apps
          </Button>
        </Flex>
      )}
    >
      {(dateRange, selectedShow) => (
        <TopPlayersModalContent
          dateRange={dateRange}
          selectedShow={selectedShow}
          granularity={granularity}
          excludeSocials={excludeSocials}
          showSocialViews={showSocialViews}
          onExcludeSocialsChange={(nextExcludeSocials) => {
            setExcludeSocials(nextExcludeSocials);
            trackAudienceTopPlayersModalHideSocialsToggled(nextExcludeSocials);
          }}
          onGranularityChange={(nextGranularity) => {
            setGranularity(nextGranularity);
            trackAudienceTopPlayersModalGranularityChanged(nextGranularity);
          }}
        />
      )}
    </ModalEcho>
  );
};

export default TopPlayersModalEcho;
